import React, { useEffect, useRef, useState, useMemo } from "react";
import "./Places.scss";
import { Col, Form } from "react-bootstrap";
import { config } from "../../../config";
import Autocomplete from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import { useLocalization, getStatesForCountry } from '../../../contexts/LocalizationContext';
import * as validate from "../helper";
import mexico from "../../../constants/mx-states";
interface AddressFormState {
  pincode: string;
  address: string;
  line2: string;
  cityValue?: string;
  stateValue?: string;
  county?: string;
  countryName: string;
  countryValue: string;
  brand?: string;
}

export interface iAddessProps {
  isDownload: boolean;
  isVerify: boolean;
  isThanks: boolean;
  validationErrors: Record<string, string>;
  isBillingReadonly: boolean;
  isShippingReadonly: boolean;
  billingAddr: any;
  formType: "billing" | "shipping"; // New prop for form type
  brand?: string; // Optional brand prop
  onPlaceUpdate: (
    data: AddressFormState,
    formType: "billing" | "shipping"
  ) => void; // Callback for sending data to parent
}
const Places: React.FC<iAddessProps> = ({
  isThanks,
  isDownload,
  isVerify,
  validationErrors,
  isBillingReadonly,
  isShippingReadonly,
  billingAddr,
  formType,
  onPlaceUpdate,
  brand
}) => {
  const isReadonly = formType === "billing" ? isBillingReadonly : isShippingReadonly;

  const inputRef = useRef<HTMLInputElement | null>(null);
  const apiKey = config.mapKey;
  const { t } = useTranslation();
  const { locale, stateDropdown, countryList, countryListPhysical, countyField, states } = useLocalization();

  const countries = useMemo(() => {
    return isDownload ? countryList : (countryListPhysical || countryList);
  }, [isDownload, countryList, countryListPhysical])

  const [selectedCountryCode, setSelectedCountryCode] = useState(countries[0].ISO_CC);

  const allowedStates = useMemo(() => {
    if (brand === 'MSI' && locale === 'en-us') {
      return states?.filter(state => state.code !== 'AK' && state.code !== 'HI');
    }
    if (locale === 'en-af' || locale === 'es-la') {
      return getStatesForCountry(selectedCountryCode);
    }
    return states;
  }, [brand, locale, states, selectedCountryCode]);
  const [addressState, setAddressState] = useState<AddressFormState>({
    pincode: "",
    address: "",
    line2: "",
    cityValue: locale === 'en-sg' ? "Singapore" : "",
    ...((stateDropdown || locale === 'zh-tw') && { stateValue: locale === 'zh-tw' ? "TW" : "" }),
    ...(countyField && { county: "" }),
    countryName: countries[0].countryName,
    countryValue: countries[0].ISO_CC,
  });


  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.setAttribute("autoComplete", "none");
    }
  });

  const handlePlaceSelect = (place: any) => {
    const addressComponents = place.address_components;

    let newPincode = (locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) ? "00000" : addressState.pincode || "";
    let newAddress = "";
    let geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
    let geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
    let geoCountyValue = addressState.county || "";
    let geoCountryValue = addressState.countryValue;
    let geoCountryName = addressState.countryName;

    if (addressComponents) {
      // Handle Google Places autocomplete selection
      let streetNumber = "";
      let route = "";
      let neighborhood = "";
      let sublocality = "";

      for (const component of addressComponents) {
        if (component.types.includes("street_number")) {
          streetNumber = component.long_name;
        }
        if (component.types.includes("route")) {
          route = component.long_name;
        }
        if (component.types.includes("neighborhood")) {
          neighborhood = (newAddress ? ", " : "") + component.long_name;
        }
        if (component.types.includes("sublocality") || component.types.includes("sublocality_level_1")) {
          if (locale === 'en-ph') {
            // Barangay mapping
            neighborhood = component.long_name;
          } else {
            sublocality = (newAddress ? ", " : "") + component.long_name;
          }
        }
        if (selectedCountryCode.toLowerCase() !== 'us' && component.types.includes("postal_town")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("locality")) {
          geoCityValue = component.long_name;
        }
        if (component.types.includes("postal_code")) {
          newPincode = component.long_name; // rare for HK
        }
        if (component.types.includes("administrative_area_level_1")) {
          if (locale === 'zh-tw' || locale === 'ko-kr') {
            geoCityValue = component.long_name;
          } else {
            // ✅ United States or default case using dropdown validation
            if (stateDropdown) {
              const stateCode = component.short_name;
              // Verify if the state code exists in geoStates
              const validState = allowedStates?.find(state => state.code === stateCode);
              if (validState) {
                geoStateValue = stateCode
              }
            }
            if (locale === 'zh-tw') {
              geoStateValue = 'TW'
            }
            if (locale === 'en-ph' && component.short_name === "NCR") {
              geoStateValue = component.short_name; // Province short code (PH)
            }
            // ✅ Special handling for Mexican states
            if (locale === 'es-mx' || component.short_name.endsWith('.')) {
              const normalized = mexico.find(mx =>
                mx.name.toLowerCase().replace(/\s|\(.*?\)/g, '') === component.long_name.toLowerCase().replace(/\s|\(.*?\)/g, '')
              );
              if (normalized) {
                geoStateValue = normalized.code;
              }
            }
          }
        }
        // ✅ Special handling for Brazilian cities
        if (component.types.includes("administrative_area_level_2") && (locale === 'en-me' || locale === 'pt-br' || locale === 'en-ph' || locale === 'en-af')) {
          if (locale === 'en-ph' || locale === 'en-af') {
            geoStateValue = component.short_name; // Province short code (PH)
          } else {
            geoCityValue = component.long_name;
          }
        }
        if (component.types.includes("country")) {
          const countryCode = component.short_name.toUpperCase();
          const validCountry = countries.find(country => country.ISO_CC === countryCode);
          if (validCountry) {
            geoCountryValue = countryCode;
            setSelectedCountryCode(countryCode);
            geoCountryName = validCountry?.countryName || "";
          }
        }
        if (component.types.includes("postal_code")) {
          newPincode = component.long_name; // rare for HK
        }
        // 🔹 Hong Kong-specific handling
        if (geoCountryValue === "HK") {
          // Highest priority: neighborhood, then sublocality, then locality
          if (component.types.includes("neighborhood")) {
            geoCityValue = component.long_name; // e.g., "Soho"
          } else if (component.types.includes("sublocality_level_1")) {
            geoCityValue = component.long_name; // e.g., "Tsim Sha Tsui"
          } else if (component.types.includes("locality")) {
            geoCityValue = component.long_name; // e.g., "Kowloon"
          }

          if (locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) {
            newPincode = "00000";
          }
        }
      }
      // Combine street number and route to create the address
      if (streetNumber || route) {
        newAddress = streetNumber + " " + route;
      } else if (streetNumber) {
        newAddress = streetNumber;
      } else if (route) {
        newAddress = route;
      }

      // Append neighborhood and sublocality for Middle East locale when both exist
      if (locale === 'en-me' && neighborhood && sublocality) {
        newAddress = (newAddress ? newAddress + ", " : "") + `${neighborhood}, ${sublocality}`;
      }

      if (!(newAddress?.length > 0) || !(validate.validateStringOrIntegerAddress(newAddress, locale))) {
        newAddress = place.formatted_address.split(",")[0] || "";
        if (locale === 'ja-jp') {
          const parts = place.formatted_address.split(",");
          if (parts[0].indexOf("Japan") > -1) {
            newAddress = parts[parts.length - 2] + parts[parts.length - 1];
          } else {
            newAddress = parts[0] + parts[1];
          }
        } else if (locale !== 'es-es' && locale !== 'it-it' && locale !== 'en-gb') {
          newAddress = place.formatted_address.split(",")[0] || "";
        } else {
          newAddress = place.formatted_address.split(",")[0] + place.formatted_address.split(",")[1];
        }
      }
    } else if (place.target) {
      // Handle form field changes
      newPincode = locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode || "";
      newAddress = addressState.address || "";
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      geoCountyValue = addressState.county || "";
      const { name, value } = place.target;
      // Reset location-specific fields when country changes
      newPincode = locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode || "";
      newAddress = addressState.address || "";
      geoCityValue = locale === 'en-sg' ? "Singapore" : addressState.cityValue || "";
      geoStateValue = locale === 'zh-tw' ? "TW" : addressState.stateValue || "";
      geoCountyValue = addressState.county || "";
      geoCountryValue = selectedCountryCode || "";
      geoCountryName = countries.find(country => country.ISO_CC === selectedCountryCode)?.countryName || "";

      if (name === `countryValue${formType === "billing" ? "Billing" : "Shipping"}`) {
        // Handle country dropdown change
        const selectedCountry = countries.find(country => country.ISO_CC === value);
        if (selectedCountry) {
          geoCountryValue = selectedCountry.ISO_CC;
          geoCountryName = selectedCountry.countryName;

          if (locale === 'en-eu' || locale === 'en-af' || locale === 'de-de' || locale === 'es-la' || locale === 'en-gb') {
            // Reset location-specific fields when country changes
            newPincode = "";
            newAddress = "";
            geoCityValue = "";
            geoStateValue = "";
            geoCountyValue = "";
          }

          // Update selected country code for Places API
          setSelectedCountryCode(value === 'ME' ? 'AE' : value);

          // Reset Google Places input
          if (inputRef.current) {
            inputRef.current.value = '';
          }
        }
      } else if (name === `address${formType === "billing" ? "Billing" : "Shipping"}`) {
        newAddress = value.split(",")[0];
      } else if (name === `city${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoCityValue = value;
      } else if (name === `stateName${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoStateValue = locale === 'zh-tw' ? "TW" : value;
      } else if (name === `county${formType === "billing" ? "Billing" : "Shipping"}`) {
        geoCountyValue = value;
      } else if (name === `zip${formType === "billing" ? "Billing" : "Shipping"}`) {
        newPincode = value;
        if (locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA')) {
          newPincode = "00000";
        }
      }
    }

    // Update address state
    const newAddressState = {
      ...addressState,
      pincode: newPincode,
      address: newAddress,
      cityValue: geoCityValue,
      ...((locale === 'zh-tw' || stateDropdown) && { stateValue: locale === 'zh-tw' ? "TW" : geoStateValue }),
      ...(countyField && { county: geoCountyValue }),
      countryName: geoCountryName,
      countryValue: geoCountryValue,
    };

    setAddressState(newAddressState);
  };

  useEffect(() => {
    let addrData: any = {
      ...{ pincode: locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : addressState.pincode },
      ...{ address: addressState.address },
      ...{ line2: addressState.line2 },
      ...{ cityValue: addressState.cityValue },
      ...((stateDropdown || locale === 'zh-tw') && { stateValue: locale === 'zh-tw' ? "TW" : addressState.stateValue }),
      ...(countyField && { county: addressState.county }),
      ...{ countryName: addressState.countryName },
      ...{ countryValue: addressState.countryValue },
    }

    if (!addressState.pincode && !addressState.address && !addressState.cityValue && !addressState.stateValue) {
      addrData = {}
    };
    if (Object.keys(addrData).length > 0) {
      onPlaceUpdate(addrData, formType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressState]);
  useEffect(() => {
    if (billingAddr) {
      const {
        postalCode,
        line1,
        city,
        countrySubdivision,
        county,
        countryName,
        country,
        line2,
      } = JSON.parse(billingAddr);
      setAddressState({
        ...addressState,
        pincode: locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : postalCode,
        address: line1,
        line2: line2,
        cityValue: locale === 'en-sg' ? "Singapore" : city,
        stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
        county: county || '',
        countryName: countryName,
        countryValue: country,
      });

      onPlaceUpdate(
        {
          pincode: locale === 'en-hk' || (locale === 'es-la' && addressState['countryValue'] === 'PA') ? "00000" : postalCode,
          address: line1,
          line2: line2,
          cityValue: locale === 'en-sg' ? "Singapore" : city,
          stateValue: locale === 'zh-tw' ? "TW" : countrySubdivision || '',
          county: county || '',
          countryName: countryName,
          countryValue: country,
        },
        formType
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddr, formType]);
  return (
    <div className={`Places ${formType === "billing" ? "billing" : "shipping"}`}>
      <Col md={6}><Form.Group controlId={`address${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`address${formType === "billing" ? "Billing" : "Shipping"}`}>{t("ADDRESS1_COLON")}</Form.Label>
        {locale === 'vi-vn' ? (
          <Form.Control
            id={`address${formType === "billing" ? "Billing" : "Shipping"}`}
            placeholder={t("ADDRESS1_REQUIRED")}
            className={`form-control ${validationErrors[`${formType}address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
            type="text"
            autoComplete="none"
            name={`address${formType === "billing" ? "Billing" : "Shipping"}`}
            readOnly={isReadonly}
            value={addressState.address}
            isInvalid={!!validationErrors[`${formType}address`]}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceSelect}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}address`]}
            aria-describedby={validationErrors[`${formType}address`] ? `address${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          />
        ) : (
          config && (
            <Autocomplete
              id={`address${formType === "billing" ? "Billing" : "Shipping"}`}
              name={`address${formType === "billing" ? "Billing" : "Shipping"}`}
              className={`form-control ${validationErrors[`${formType}address`] ? "is-invalid" : ""}  ${isReadonly ? "disabled" : ""} `}
              ref={inputRef}
              placeholder={t("ADDRESS1_REQUIRED")}
              apiKey={apiKey}
              onPlaceSelected={handlePlaceSelect}
              onBlur={handlePlaceSelect}
              onChange={handlePlaceSelect}
              options={{
                types: ["geocode", "establishment"],
                componentRestrictions: { country: selectedCountryCode },
              }}
              defaultValue={addressState.address}
              value={addressState.address}
              autoComplete="new-password"
              readOnly={isReadonly}
              disabled={isReadonly}
              inputProps={{
                ref: inputRef,
                autoComplete: 'none'
              }}
              aria-required="true"
              aria-invalid={!!validationErrors[`${formType}address`]}
              aria-describedby={validationErrors[`${formType}address`] ? `address${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
            />
          )
        )}
        {validationErrors[`${formType}address`] && (
          <Form.Control.Feedback type="invalid" id={`address${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}address`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
      <Col md={6}><Form.Group controlId={`line2${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`line2${formType === "billing" ? "Billing" : "Shipping"}`}>{t("LINE2_OPTIONAL_COLON")}</Form.Label>
        <Form.Control
          id={`line2${formType === "billing" ? "Billing" : "Shipping"}`}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          placeholder={t("LINE2_OPTIONAL")}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`line2${formType === "billing" ? "Billing" : "Shipping"}`}
          value={addressState.line2}
          onChange={(event: any) => {
            setAddressState({
              ...addressState,
              line2: event.target.value,
            })
          }}
        />
      </Form.Group>
      </Col>
      {locale !== 'en-sg' && (<Col md={6}><Form.Group controlId={`city${formType === "billing" ? "Billing" : "Shipping"}`}>
        <Form.Label className="visually-hidden" htmlFor={`city${formType === "billing" ? "Billing" : "Shipping"}`}>{t("CITY_COLON")}</Form.Label>
        <Form.Control
          id={`city${formType === "billing" ? "Billing" : "Shipping"}`}
          placeholder={locale === 'en-sg' ? t("CITY") : t("CITY_REQUIRED")}
          type="text"
          autoComplete="none"
          readOnly={isReadonly}
          className={`form-control  ${isReadonly ? "disabled" : ""} `}
          name={`city${formType === "billing" ? "Billing" : "Shipping"}`}
          value={addressState.cityValue}
          isInvalid={!!validationErrors[`${formType}cityValue`]}
          onChange={handlePlaceSelect}
          onBlur={handlePlaceSelect}
          aria-required="true"
          aria-invalid={!!validationErrors[`${formType}cityValue`]}
          aria-describedby={validationErrors[`${formType}cityValue`] ? `city${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
        />
        {validationErrors[`${formType}cityValue`] && (
          <Form.Control.Feedback type="invalid" id={`city${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}cityValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>)}

      {(stateDropdown) && (
        <Col md={6}>
          <Form.Group controlId={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}>
            <Form.Label className="visually-hidden" htmlFor={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}>{t("STATE")}</Form.Label>
            <Form.Select
              id={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}
              autoComplete="none"
              value={addressState.stateValue}
              disabled={isReadonly}
              className={`form-control  ${isReadonly ? "disabled" : ""} `}
              name={`stateName${formType === "billing" ? "Billing" : "Shipping"}`}
              onChange={handlePlaceSelect}
              onBlur={handlePlaceSelect}
              isInvalid={!!validationErrors[`${formType}stateValue`]}
              aria-required="true"
              aria-invalid={!!validationErrors[`${formType}stateValue`]}
              aria-describedby={validationErrors[`${formType}stateValue`] ? `stateName${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
            >
              <option value="">{t("SELECT_STATE")}</option>
              {allowedStates?.map((geoState, index) => (
                <option key={index} value={geoState.code}>
                  {geoState.name}
                </option>
              ))}
            </Form.Select>
            {validationErrors[`${formType}stateValue`] && (
              <Form.Control.Feedback type="invalid" id={`stateName${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
                {validationErrors[`${formType}stateValue`]}
              </Form.Control.Feedback>
            )}
          </Form.Group>
        </Col>)}
      {locale !== 'en-hk' && locale !== 'en-me' && (<Col md={6}>
        <Form.Group controlId={`zip${formType === "billing" ? "Billing" : "Shipping"}`}>
          <Form.Label className="visually-hidden" htmlFor={`zip${formType === "billing" ? "Billing" : "Shipping"}`}>{t("ZIP_CODE_COLON")}</Form.Label>
          <Form.Control
            id={`zip${formType === "billing" ? "Billing" : "Shipping"}`}
            placeholder={t("ZIP_CODE_REQUIRED")}
            type="text"
            autoComplete="none"
            readOnly={isReadonly}
            className={`form-control  ${isReadonly ? "disabled" : ""} `}
            name={`zip${formType === "billing" ? "Billing" : "Shipping"}`}
            value={addressState.pincode}
            isInvalid={!!validationErrors[`${formType}pincode`]}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceSelect}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}pincode`]}
            aria-describedby={validationErrors[`${formType}pincode`] ? `zip${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          />
          {validationErrors[`${formType}pincode`] && (
            <Form.Control.Feedback type="invalid" id={`zip${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
              {validationErrors[`${formType}pincode`]}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Col>)}
      <Col md={6}><Form.Group controlId={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
      >
        <div className={`${countries.length === 1 ? "singlecountry" : ""}`}>
          <Form.Label className="visually-hidden" htmlFor={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}>{t("COUNTRY_REQUIRED")}</Form.Label>
          <Form.Select
            id={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
            autoComplete="none"
            className={`form-control  ${isReadonly || countries.length === 1 ? "disabled" : ""} `}
            name={`countryValue${formType === "billing" ? "Billing" : "Shipping"}`}
            disabled={isReadonly || countries.length === 1}
            value={addressState.countryValue}
            onChange={handlePlaceSelect}
            onBlur={handlePlaceSelect}
            isInvalid={!!validationErrors[`${formType}countryValue`]}
            aria-required="true"
            aria-invalid={!!validationErrors[`${formType}countryValue`]}
            aria-describedby={validationErrors[`${formType}countryValue`] ? `countryValue${formType === "billing" ? "Billing" : "Shipping"}Error` : undefined}
          >

            {countries.map((country) => (
              <option key={country.ISO_CC} value={country.ISO_CC}>
                {country.countryName}
              </option>
            ))}

          </Form.Select>
        </div>
        {validationErrors[`${formType}countryValue`] && (
          <Form.Control.Feedback type="invalid" id={`countryValue${formType === "billing" ? "Billing" : "Shipping"}Error`} aria-live="polite">
            {validationErrors[`${formType}countryValue`]}
          </Form.Control.Feedback>
        )}
      </Form.Group>
      </Col>
    </div>
  );
};
export default Places;